import { loadPref, mediaUrl, savePref } from '../os/prefs';
import type { Track } from '../types/content';

let ctx: AudioContext | null = null;
let ambientNode: { stop: () => void } | null = null;
let uiSoundsOn = true;
let masterMute = false;
let playlist: Track[] = [];
let trackIndex = -1;
let musicEl: HTMLAudioElement | null = null;
let wantMusic = false;
let musicVolume = loadVolume();
const musicListeners = new Set<() => void>();

function loadVolume(): number {
  const raw = Number(loadPref('desk.musicVol', '0.8'));
  if (!Number.isFinite(raw)) return 0.8;
  return Math.min(1, Math.max(0, raw));
}

function wireMusicEl(el: HTMLAudioElement, index: number): void {
  el.volume = musicVolume;
  el.addEventListener('ended', () => playTrackAt(index + 1));
  el.addEventListener('play', notifyMusic);
  el.addEventListener('pause', notifyMusic);
  el.addEventListener('timeupdate', notifyMusic);
  el.addEventListener('loadedmetadata', notifyMusic);
  el.addEventListener('durationchange', notifyMusic);
}

export function setUiSounds(on: boolean): void {
  uiSoundsOn = on;
}

export function onMusicChange(fn: () => void): () => void {
  musicListeners.add(fn);
  return () => musicListeners.delete(fn);
}

function notifyMusic(): void {
  for (const fn of musicListeners) fn();
}

export function setPlaylist(tracks: Track[]): void {
  playlist = tracks;
}

export function getMusicNow(): {
  track: Track | null;
  index: number;
  playing: boolean;
  currentTime: number;
  duration: number;
  volume: number;
} {
  const track = trackIndex >= 0 ? (playlist[trackIndex] ?? null) : null;
  const fromFile = musicEl && Number.isFinite(musicEl.duration) ? musicEl.duration : 0;
  const duration = fromFile > 0 ? fromFile : (track?.durationSec ?? 0);
  return {
    track,
    index: trackIndex,
    playing: Boolean(wantMusic && musicEl && !musicEl.paused),
    currentTime: musicEl?.currentTime ?? 0,
    duration,
    volume: musicVolume,
  };
}

function applyMusic(): void {
  if (!musicEl) return;
  if (masterMute || !wantMusic) {
    musicEl.pause();
    notifyMusic();
    return;
  }
  void musicEl.play().catch(() => {
    /* autoplay block */
  });
  notifyMusic();
}

export function playTrackAt(index: number): void {
  if (!playlist.length) return;
  const i = ((index % playlist.length) + playlist.length) % playlist.length;
  const track = playlist[i];
  if (!track) return;
  musicEl?.pause();
  musicEl = new Audio(mediaUrl(track.src));
  wireMusicEl(musicEl, i);
  trackIndex = i;
  wantMusic = true;
  applyMusic();
}

export function playMusic(): void {
  if (!playlist.length) return;
  if (trackIndex < 0) {
    playTrackAt(0);
    return;
  }
  wantMusic = true;
  applyMusic();
}

export function pauseMusic(): void {
  wantMusic = false;
  applyMusic();
}

export function setMusicVolume(value: number): void {
  const next = Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : 0.8;
  musicVolume = next;
  savePref('desk.musicVol', String(next));
  if (musicEl) musicEl.volume = next;
  notifyMusic();
}

export function stopMusic(): void {
  wantMusic = false;
  if (musicEl) {
    musicEl.pause();
    musicEl.currentTime = 0;
  }
  notifyMusic();
}

export function nextTrack(): void {
  playTrackAt(trackIndex < 0 ? 0 : trackIndex + 1);
}

export function prevTrack(): void {
  playTrackAt(trackIndex < 0 ? 0 : trackIndex - 1);
}

export function toggleMusic(): void {
  if (!playlist.length) return;
  if (trackIndex < 0) {
    playTrackAt(0);
    return;
  }
  wantMusic = !wantMusic;
  applyMusic();
}

function getCtx(): AudioContext | null {
  try {
    ctx ??= new AudioContext();
    return ctx;
  } catch {
    return null;
  }
}

export async function unlockAudio(): Promise<void> {
  const c = getCtx();
  if (c && c.state === 'suspended') await c.resume();
}

export function playSfx(id: 'click' | 'open' | 'minimize'): void {
  if (!uiSoundsOn || masterMute) return;
  const c = getCtx();
  if (!c) return;
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = 'square';
  osc.frequency.value = id === 'open' ? 520 : id === 'minimize' ? 220 : 340;
  g.gain.value = 0.04;
  g.gain.exponentialRampToValueAtTime(0.0001, c.currentTime + 0.08);
  osc.connect(g).connect(c.destination);
  osc.start();
  osc.stop(c.currentTime + 0.09);
}

export function setMasterMute(on: boolean): void {
  masterMute = on;
  applyMusic();
}

export function setAmbient(on: boolean, muted: boolean): void {
  ambientNode?.stop();
  ambientNode = null;
  if (!on || muted) return;
  const c = getCtx();
  if (!c) return;
  const bufferSize = 2 * c.sampleRate;
  const buffer = c.createBuffer(1, bufferSize, c.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < bufferSize; i++) data[i] = (Math.random() * 2 - 1) * 0.15;
  const src = c.createBufferSource();
  src.buffer = buffer;
  src.loop = true;
  const filter = c.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.value = 280;
  const g = c.createGain();
  g.gain.value = 0.18;
  src.connect(filter).connect(g).connect(c.destination);
  src.start();
  ambientNode = {
    stop: () => {
      try {
        src.stop();
      } catch {
        /* already stopped */
      }
    },
  };
}
